/**
 * Per-species live-weight tiers + dressing yields, seeded from USDA /
 * cooperative-extension / industry sources. Shared single source of truth for
 * the buyer cost-estimate math (`packages/api` estimate-cost) and the producer
 * size-tier picker rings (`packages/web`). Keys are `EAnimalSpecies` values;
 * `hog` carries pork data.
 *
 * `yieldPct` is the fraction of live weight retained as hanging (carcass)
 * weight — EXCEPT alligator, see its note below. Replace with real farm data
 * when available.
 *
 * Sources: MSU / Penn State (beef), U. Maine / UW-Madison (hog), UW-Madison /
 * Virginia Tech (lamb), MLA / Maryland SGM (goat), National Bison Assn (bison),
 * FAO / Deer NZ (venison), FAO (yak), J. Anim. Sci. / USDA FSIS (veal).
 * Wild game (elk, moose, antelope, bear) are state wildlife-agency and
 * cooperative-extension field-dressing figures — wider natural variation than
 * farmed stock, so treat them as ballpark until real plant data replaces them.
 */
export type SizeTier = 'xs' | 's' | 'm' | 'l' | 'xl';

export interface YieldTier {
  tier: SizeTier;
  liveLow: number;
  liveHigh: number;
  yieldPct: number;
}

export const SPECIES_YIELD: Record<string, YieldTier[]> = {
  beef: [
    { tier: 'xs', liveLow: 800, liveHigh: 950, yieldPct: 0.58 },
    { tier: 's', liveLow: 950, liveHigh: 1100, yieldPct: 0.6 },
    { tier: 'm', liveLow: 1100, liveHigh: 1250, yieldPct: 0.62 },
    { tier: 'l', liveLow: 1250, liveHigh: 1400, yieldPct: 0.63 },
    { tier: 'xl', liveLow: 1400, liveHigh: 1600, yieldPct: 0.64 },
  ],
  hog: [
    { tier: 'xs', liveLow: 200, liveHigh: 235, yieldPct: 0.68 },
    { tier: 's', liveLow: 235, liveHigh: 270, yieldPct: 0.69 },
    { tier: 'm', liveLow: 270, liveHigh: 305, yieldPct: 0.7 },
    { tier: 'l', liveLow: 305, liveHigh: 345, yieldPct: 0.71 },
    { tier: 'xl', liveLow: 345, liveHigh: 400, yieldPct: 0.72 },
  ],
  lamb: [
    { tier: 'xs', liveLow: 60, liveHigh: 80, yieldPct: 0.48 },
    { tier: 's', liveLow: 80, liveHigh: 100, yieldPct: 0.5 },
    { tier: 'm', liveLow: 100, liveHigh: 120, yieldPct: 0.51 },
    { tier: 'l', liveLow: 120, liveHigh: 140, yieldPct: 0.52 },
    { tier: 'xl', liveLow: 140, liveHigh: 170, yieldPct: 0.53 },
  ],
  goat: [
    { tier: 'xs', liveLow: 40, liveHigh: 60, yieldPct: 0.45 },
    { tier: 's', liveLow: 60, liveHigh: 75, yieldPct: 0.475 },
    { tier: 'm', liveLow: 75, liveHigh: 90, yieldPct: 0.49 },
    { tier: 'l', liveLow: 90, liveHigh: 110, yieldPct: 0.51 },
    { tier: 'xl', liveLow: 110, liveHigh: 140, yieldPct: 0.53 },
  ],
  bison: [
    { tier: 'xs', liveLow: 700, liveHigh: 850, yieldPct: 0.53 },
    { tier: 's', liveLow: 850, liveHigh: 1000, yieldPct: 0.545 },
    { tier: 'm', liveLow: 1000, liveHigh: 1150, yieldPct: 0.56 },
    { tier: 'l', liveLow: 1150, liveHigh: 1300, yieldPct: 0.57 },
    { tier: 'xl', liveLow: 1300, liveHigh: 1500, yieldPct: 0.58 },
  ],
  // `venison` is DEER. Elk, moose, antelope, and bear are separate species with
  // their own live-weight ranges — a moose is several times a whitetail, so
  // sharing deer's ladder would put the estimate out by a wide margin.
  venison: [
    { tier: 'xs', liveLow: 150, liveHigh: 200, yieldPct: 0.55 },
    { tier: 's', liveLow: 200, liveHigh: 250, yieldPct: 0.57 },
    { tier: 'm', liveLow: 250, liveHigh: 300, yieldPct: 0.58 },
    { tier: 'l', liveLow: 300, liveHigh: 375, yieldPct: 0.59 },
    { tier: 'xl', liveLow: 375, liveHigh: 500, yieldPct: 0.6 },
  ],
  elk: [
    { tier: 'xs', liveLow: 400, liveHigh: 500, yieldPct: 0.54 },
    { tier: 's', liveLow: 500, liveHigh: 625, yieldPct: 0.55 },
    { tier: 'm', liveLow: 625, liveHigh: 750, yieldPct: 0.56 },
    { tier: 'l', liveLow: 750, liveHigh: 900, yieldPct: 0.575 },
    { tier: 'xl', liveLow: 900, liveHigh: 1100, yieldPct: 0.59 },
  ],
  moose: [
    { tier: 'xs', liveLow: 600, liveHigh: 750, yieldPct: 0.52 },
    { tier: 's', liveLow: 750, liveHigh: 900, yieldPct: 0.535 },
    { tier: 'm', liveLow: 900, liveHigh: 1050, yieldPct: 0.55 },
    { tier: 'l', liveLow: 1050, liveHigh: 1250, yieldPct: 0.565 },
    { tier: 'xl', liveLow: 1250, liveHigh: 1600, yieldPct: 0.58 },
  ],
  antelope: [
    { tier: 'xs', liveLow: 70, liveHigh: 85, yieldPct: 0.5 },
    { tier: 's', liveLow: 85, liveHigh: 100, yieldPct: 0.515 },
    { tier: 'm', liveLow: 100, liveHigh: 115, yieldPct: 0.53 },
    { tier: 'l', liveLow: 115, liveHigh: 130, yieldPct: 0.545 },
    { tier: 'xl', liveLow: 130, liveHigh: 150, yieldPct: 0.56 },
  ],
  // Bear carries a heavy fat layer, so dressing yield runs below the cervids
  // and the take-home fraction below that once fat is trimmed.
  bear: [
    { tier: 'xs', liveLow: 100, liveHigh: 175, yieldPct: 0.48 },
    { tier: 's', liveLow: 175, liveHigh: 250, yieldPct: 0.5 },
    { tier: 'm', liveLow: 250, liveHigh: 325, yieldPct: 0.515 },
    { tier: 'l', liveLow: 325, liveHigh: 400, yieldPct: 0.53 },
    { tier: 'xl', liveLow: 400, liveHigh: 550, yieldPct: 0.545 },
  ],
  yak: [
    { tier: 'xs', liveLow: 400, liveHigh: 500, yieldPct: 0.48 },
    { tier: 's', liveLow: 500, liveHigh: 600, yieldPct: 0.5 },
    { tier: 'm', liveLow: 600, liveHigh: 700, yieldPct: 0.51 },
    { tier: 'l', liveLow: 700, liveHigh: 800, yieldPct: 0.52 },
    { tier: 'xl', liveLow: 800, liveHigh: 950, yieldPct: 0.54 },
  ],
  veal: [
    { tier: 'xs', liveLow: 300, liveHigh: 375, yieldPct: 0.58 },
    { tier: 's', liveLow: 375, liveHigh: 425, yieldPct: 0.595 },
    { tier: 'm', liveLow: 425, liveHigh: 475, yieldPct: 0.61 },
    { tier: 'l', liveLow: 475, liveHigh: 525, yieldPct: 0.62 },
    { tier: 'xl', liveLow: 525, liveHigh: 600, yieldPct: 0.63 },
  ],
  // Alligator is unlike the mammals above: farmed gators have no conventional
  // carcass "hanging weight", so `yieldPct` here is EDIBLE BONELESS MEAT ÷ live
  // weight, and it trends DOWN with size (head/hide/bone/scute share grows).
  // `interpolateYield` handles the descending ladder fine (plain linear
  // interpolation). The estimate still applies the processor's per-lb cut&wrap
  // fee to this meat-basis weight — a documented approximation, not carcass math.
  alligator: [
    { tier: 'xs', liveLow: 15, liveHigh: 30, yieldPct: 0.4 },
    { tier: 's', liveLow: 30, liveHigh: 60, yieldPct: 0.38 },
    { tier: 'm', liveLow: 60, liveHigh: 120, yieldPct: 0.35 },
    { tier: 'l', liveLow: 120, liveHigh: 250, yieldPct: 0.32 },
    { tier: 'xl', liveLow: 250, liveHigh: 450, yieldPct: 0.3 },
  ],
};

export function tiersForSpecies(species?: string | null): YieldTier[] | null {
  if (!species) return null;
  return SPECIES_YIELD[species.toLowerCase()] ?? null;
}

/**
 * Cutting yield: the fraction of HANGING (carcass) weight that ends up as
 * packaged, take-home ("freezer") meat after the butcher trims bone, fat, and
 * shrink. This is a second yield stage AFTER dressing (live → hanging) and is
 * what buyers actually receive. It's inherently cutsheet-dependent (a bone-in,
 * fat-on order retains more; a lean boneless order less), so these are
 * mid-range industry approximations — not a promise — surfaced to buyers with a
 * "varies by cutsheet" caveat.
 *
 * Alligator is special: its `SPECIES_YIELD.yieldPct` is already edible-meat ÷
 * live weight (see the table note), so its "hanging" basis is effectively meat
 * already — cutting yield is 1.0 (no further carcass-to-freezer step).
 *
 * Sources: typical beef/pork/lamb "freezer weight ≈ X% of hanging" figures from
 * cooperative-extension buyer guides (MSU, Penn State, UW-Madison). Replace with
 * real per-processor cutting data when available.
 */
export const TAKE_HOME_YIELD: Record<string, number> = {
  beef: 0.62,
  bison: 0.62,
  hog: 0.72,
  lamb: 0.72,
  goat: 0.7,
  venison: 0.62,
  elk: 0.62,
  moose: 0.61,
  antelope: 0.6,
  // Lower than the cervids: a bear's fat layer is trimmed off rather than
  // packaged, so less of the carcass reaches the freezer.
  bear: 0.55,
  yak: 0.62,
  veal: 0.66,
  alligator: 1.0,
};

/**
 * Fraction of hanging weight that becomes take-home packaged meat for a
 * species, or `null` when the species is unconfigured (caller then shows the
 * hanging weight alone, without a freezer-weight estimate).
 * @param species - The animal species (case-insensitive).
 * @returns The take-home yield fraction, or null when unconfigured.
 */
export function takeHomeYieldFor(species?: string | null): number | null {
  if (!species) return null;
  return TAKE_HOME_YIELD[species.toLowerCase()] ?? null;
}

/**
 * Human-readable live-weight ring for a species + tier, e.g. `"1,100–1,250 lb"`.
 * Returns `null` when the species is unconfigured or the tier is unknown, so
 * callers can fall back to a bare tier label.
 * @param species - The animal species (case-insensitive).
 * @param tier - The weight-tier name (case-insensitive).
 * @returns The formatted live-weight range, or null when unknown.
 */
export function formatTierRing(
  species: string | null | undefined,
  tier: string,
): string | null {
  const tiers = tiersForSpecies(species);
  if (!tiers) return null;
  const match = tiers.find((t) => t.tier === tier.toLowerCase());
  if (!match) return null;
  return `${match.liveLow.toLocaleString()}–${match.liveHigh.toLocaleString()} lb`;
}

const midpoint = (t: YieldTier) => (t.liveLow + t.liveHigh) / 2;

/**
 * Linear interpolation of dressing yield across tier midpoints, clamped to the
 * first/last tier yield outside the midpoint span.
 * @param tiers - The species' weight tiers.
 * @param liveWeight - The live weight in pounds.
 * @returns The interpolated dressing-yield fraction (0 for no tiers).
 */
export function interpolateYield(
  tiers: YieldTier[],
  liveWeight: number,
): number {
  if (tiers.length === 0) return 0;
  const sorted = [...tiers].sort((a, b) => midpoint(a) - midpoint(b));
  if (liveWeight <= midpoint(sorted[0])) return sorted[0].yieldPct;
  const last = sorted[sorted.length - 1];
  if (liveWeight >= midpoint(last)) return last.yieldPct;
  for (let i = 0; i < sorted.length - 1; i++) {
    const lo = sorted[i];
    const hi = sorted[i + 1];
    const loMid = midpoint(lo);
    const hiMid = midpoint(hi);
    if (liveWeight >= loMid && liveWeight <= hiMid) {
      const frac = (liveWeight - loMid) / (hiMid - loMid);
      return lo.yieldPct + frac * (hi.yieldPct - lo.yieldPct);
    }
  }
  return last.yieldPct;
}
